import {
  html,
  type ReactiveController,
  type ReactiveControllerHost,
  type TemplateResult,
} from "lit";
import { cn } from "@/utilities/css";
import { CSAT_RATINGS, type CsatRating, type FeedbackWidgetSubmitDetail } from "../types";

interface CsatFeedbackCallbacks {
  onCancel: (e: Event) => void;
  onSubmit: (detail: FeedbackWidgetSubmitDetail) => void;
}

export class CsatFeedbackController implements ReactiveController {
  private _csatDontAsk = false;
  private _csatFeedback = "";
  private _csatRating: number | undefined = undefined;

  private _onDontAskChange = (e: CustomEvent<{ checked: boolean }>) => {
    this._csatDontAsk = e.detail.checked;
    this.host.requestUpdate();
  };

  private _onFeedbackInput = (e: CustomEvent<{ value: string }>) => {
    if (typeof e.detail?.value === "string") {
      this._csatFeedback = e.detail.value;
      this.host.requestUpdate();
    }
  };

  private _onSubmit = () => {
    if (!this._csatRating && !this._csatDontAsk) return;
    this.callbacks.onSubmit({
      dontAskAgain: this._csatDontAsk,
      feedback: this._csatFeedback,
      feedbackType: "csat",
      rating: this._csatRating,
    });
  };

  constructor(
    private host: ReactiveControllerHost & HTMLElement,
    private callbacks: CsatFeedbackCallbacks
  ) {
    host.addController(this);
  }

  private _onRatingKeydown(e: KeyboardEvent, value: number) {
    const ratings = Array.from(
      this.host.shadowRoot!.querySelectorAll<HTMLElement>(".eko-feedback-widget__csat-rating")
    );
    const index = CSAT_RATINGS.findIndex((r) => r.value === value);

    switch (e.key) {
      case "ArrowDown":
      case "ArrowRight": {
        e.preventDefault();
        const nextIndex = (index + 1) % ratings.length;
        this._csatRating = CSAT_RATINGS[nextIndex].value;
        this.host.requestUpdate();
        this.host.updateComplete.then(() => ratings[nextIndex].focus());
        break;
      }
      case "ArrowUp":
      case "ArrowLeft": {
        e.preventDefault();
        const prevIndex = (index - 1 + ratings.length) % ratings.length;
        this._csatRating = CSAT_RATINGS[prevIndex].value;
        this.host.requestUpdate();
        this.host.updateComplete.then(() => ratings[prevIndex].focus());
        break;
      }
      case " ":
      case "Enter":
        e.preventDefault();
        this._csatRating = value;
        this.host.requestUpdate();
        break;
    }
  }

  private _renderRating(rating: CsatRating): TemplateResult {
    const selected = this._csatRating === rating.value;
    const tabindex = selected || (!this._csatRating && rating.value === 1) ? 0 : -1;
    return html`
      <div
        class=${cn("eko-feedback-widget__csat-rating", {
          "eko-feedback-widget__csat-rating--selected": selected,
        })}
        role="radio"
        aria-checked=${String(selected)}
        aria-label=${rating.label}
        tabindex=${tabindex}
        @click=${() => {
          this._csatRating = rating.value;
          this.host.requestUpdate();
        }}
        @keydown=${(e: KeyboardEvent) => this._onRatingKeydown(e, rating.value)}>
        ${rating.emoji}
      </div>
    `;
  }

  private _resolvePlaceholder(
    negative: string | undefined,
    neutral: string | undefined,
    positive: string | undefined
  ): string {
    if (this._csatRating !== undefined) {
      if (this._csatRating <= 2 && negative) return negative;
      if (this._csatRating === 3 && neutral) return neutral;
      if (this._csatRating >= 4 && positive) return positive;
    }
    return "Provide feedback (optional)";
  }

  hostConnected() {}

  render(params: {
    errorMessage: string | undefined;
    followUpNegative: string | undefined;
    followUpNeutral: string | undefined;
    followUpPositive: string | undefined;
    loading: boolean;
    question: string;
  }): TemplateResult {
    const { errorMessage, followUpNegative, followUpNeutral, followUpPositive, loading, question } =
      params;

    const placeholder = this._resolvePlaceholder(
      followUpNegative,
      followUpNeutral,
      followUpPositive
    );

    return html`
      <div slot="title" class="eko-feedback-widget__csat-header">
        <span class="eko-feedback-widget__csat-label">
          <eko-icon name="message-question-circle" size="sm"></eko-icon>
          Help us improve your experience
        </span>
        <span class="eko-feedback-widget__csat-question">${question}</span>
      </div>

      <div class="eko-feedback-widget__csat-body">
        <div class="eko-feedback-widget__csat-ratings" role="radiogroup" aria-label=${question}>
          ${CSAT_RATINGS.map((r) => this._renderRating(r))}
        </div>
        ${errorMessage ? html`<eko-alert variant="danger">${errorMessage}</eko-alert>` : html``}
        <eko-textarea
          placeholder=${placeholder}
          .value=${this._csatFeedback}
          .rows=${3}
          @input=${this._onFeedbackInput}></eko-textarea>
      </div>

      <div slot="footer" class="eko-feedback-widget__csat-footer">
        <eko-checkbox .checked=${this._csatDontAsk} @change=${this._onDontAskChange}>
          <span slot="label">Don't ask me again</span>
        </eko-checkbox>
        <eko-button
          variant="primary"
          ?disabled=${(!this._csatRating && !this._csatDontAsk) || loading}
          ?loading=${loading}
          @click=${this._onSubmit}>
          Submit
        </eko-button>
      </div>
    `;
  }

  reset() {
    this._csatRating = undefined;
    this._csatFeedback = "";
    this._csatDontAsk = false;
  }
}
